import { LocalDataError } from '../types.js';
import { EXPECTED_TABLES, MIGRATIONS } from './schema.js';
import { nowIso } from '../domain/time.js';
const TRACKING_TABLE = `CREATE TABLE IF NOT EXISTS schema_migrations (
  version INTEGER PRIMARY KEY,
  name TEXT NOT NULL,
  applied_at TEXT NOT NULL
)`;
export async function migrate(driver, migrations = MIGRATIONS) {
    assertOrdered(migrations);
    await driver.execute({ sql: TRACKING_TABLE });
    const rows = await driver.query({
        sql: 'SELECT version, name FROM schema_migrations ORDER BY version',
    });
    const appliedVersions = new Map(rows.map((r) => [Number(r.version), String(r.name)]));
    const latestKnown = migrations[migrations.length - 1]?.version ?? 0;
    const current = rows.length ? Math.max(...appliedVersions.keys()) : 0;
    if (current > latestKnown) {
        throw new LocalDataError('SCHEMA_TOO_NEW', `Database is at schema version ${current} but this build only knows ${latestKnown}. ` +
            'Refusing to open a newer database with older code.');
    }
    const applied = [];
    for (const migration of migrations) {
        const recorded = appliedVersions.get(migration.version);
        if (recorded !== undefined) {
            if (recorded !== migration.name) {
                throw new LocalDataError('MIGRATION_MISMATCH', `Migration v${migration.version} was applied as "${recorded}" but the code defines ` +
                    `"${migration.name}". Shipped migrations must never be edited.`);
            }
            continue;
        }
        // The migration's DDL and its tracking row commit or roll back together.
        await driver.batch([
            ...migration.statements.map((sql) => ({ sql })),
            {
                sql: 'INSERT INTO schema_migrations (version, name, applied_at) VALUES (?, ?, ?)',
                params: [migration.version, migration.name, nowIso()],
            },
        ]);
        applied.push(migration.version);
    }
    const version = migrations[migrations.length - 1]?.version ?? current;
    // Mirror into user_version so native code can read the level without SQL.
    await driver.execute({ sql: `PRAGMA user_version = ${version}` });
    return { applied, version };
}
/** Post-migration validation: structure present, referential integrity clean. */
export async function validateSchema(driver) {
    const tables = await driver.query({
        sql: `SELECT name FROM sqlite_master WHERE type = 'table'`,
    });
    const names = new Set(tables.map((t) => String(t.name)));
    const missing = EXPECTED_TABLES.filter((t) => !names.has(t));
    if (missing.length > 0) {
        throw new LocalDataError('SCHEMA_INVALID', `Missing tables after migration: ${missing.join(', ')}`);
    }
    const integrity = await driver.query({ sql: 'PRAGMA integrity_check' });
    const verdict = String(integrity[0]?.integrity_check ?? '');
    if (verdict !== 'ok') {
        throw new LocalDataError('DB_CORRUPT', `integrity_check failed: ${verdict}`);
    }
    const fkViolations = await driver.query({ sql: 'PRAGMA foreign_key_check' });
    if (fkViolations.length > 0) {
        throw new LocalDataError('DB_FK_VIOLATION', `foreign_key_check reported ${fkViolations.length} violation(s)`);
    }
    const fkOn = await driver.query({ sql: 'PRAGMA foreign_keys' });
    if (Number(fkOn[0]?.foreign_keys) !== 1) {
        throw new LocalDataError('DB_MISCONFIGURED', 'foreign_keys pragma is not ON for this connection');
    }
}
function assertOrdered(migrations) {
    for (let i = 0; i < migrations.length; i += 1) {
        const expected = i + 1;
        if (migrations[i]?.version !== expected) {
            throw new LocalDataError('MIGRATIONS_MISORDERED', `Migration list must be 1..n without gaps; index ${i} has version ${migrations[i]?.version}`);
        }
    }
}
//# sourceMappingURL=migrations.js.map